import React from 'react';
import { styled } from '@compiled/react';
import Button, { type Appearance } from '@atlaskit/button';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import type { ValidationError } from '../../types/validation.tsx';
import { messages as fieldMessages } from '../field-active/messages.tsx';

type ChevronProps = {
	isOpen: boolean;
	isExpandable: boolean;
	onClick: (e: React.MouseEvent<HTMLElement>) => void;
	/**
	 * When error is defined, the appearance will automatically switch to 'danger'.
	 * When undefined, the appearance will equal 'subtle-link'.
	 */
	appearance?: Appearance;
	/**
	 * When error is undefined it means the field is valid;
	 * When error is ValidationError then the chevron becomes an ErrorIcon instead,
	 * and automatically renders with the appearance 'danger'.
	 *
	 * If error is defined then the `appearance` prop is ignored.
	 */
	error: ValidationError | undefined;
	testId?: string;
	name?: string;
};

const getErrorMessage = (error: ValidationError, intl: IntlShape): string =>
	intl.formatMessage(error.messageDescriptor, error.values);

export const FieldChevron = ({
	isOpen,
	isExpandable,
	onClick,
	appearance,
	error,
	testId,
	name,
}: ChevronProps) => {
	const intl = useIntl();
	if (!isExpandable) {
		return null;
	}

	// toggleFieldButton descriptor will handle with/without name along with open/close
	const labelWithName = intl.formatMessage(fieldMessages.toggleFieldButton, {
		field: name || null,
		isOpen,
	});

	if (error != null) {
		return (
			<Tooltip content={getErrorMessage(error, intl)}>
				<FixedChevron>
					<Button
						spacing="none"
						appearance="danger"
						onClick={onClick}
						iconBefore={
							// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
							<ErrorIcon
								testId={testId ? `${testId}--error` : undefined}
								secondaryColor={token('color.icon.danger')}
								primaryColor={token('color.background.accent.gray.subtlest')}
								label=""
							/>
						}
						aria-label={labelWithName}
					/>
				</FixedChevron>
			</Tooltip>
		);
	}

	return (
		/**
		 * Note that data-test-cypress is being hardcoded for jira/src/packages/project-settings/issue-type-page/src/integration-tests/cy-integration.tsx
		 * and is being defined by CY_FIELD_TYPE_TOGGLE_PANEL_BUTTON in jira/src/packages/project-settings/issue-type-page/src/integration-tests/page-object.tsx
		 */
		<RotatingChevron data-test-cypress="field-type-toggle-panel-button" isOpen={isOpen}>
			<Button
				spacing="none"
				appearance={appearance || 'subtle-link'}
				onClick={onClick}
				iconBefore={
					<ChevronRightIcon
						spacing="spacious"
						LEGACY_fallbackIcon={ChevronRightLargeIcon}
						LEGACY_primaryColor={token('color.icon')}
						label=""
					/>
				}
				aria-label={labelWithName}
			/>
		</RotatingChevron>
	);
};

const elementSize = token('space.300');
const animationParams = '250ms cubic-bezier(0.15, 1, 0.3, 1)';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RotatingChevron = styled.span<{ ['data-test-cypress']: string; isOpen: boolean }>({
	transition: `transform ${animationParams}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	transform: (props) => (props.isOpen ? 'rotate(90deg)' : 'none'),
	height: elementSize,
	width: elementSize,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FixedChevron = styled.span({
	height: elementSize,
	width: elementSize,
});
